import { Dispatch, useCallback, useEffect, useRef, useState } from 'react';
import {
  convertRectangePointToYolo,
  getScaledCoordinates,
} from '@/utils/ia.utils';
import {
  getCanvasContext,
  initializeCanvas,
  drawRectangle,
  drawPolygon,
} from '@/utils/canvas.utils';
import { TResultImageCoordinatesSchema } from '@/schemas/vehicule-check-result/result-image-coordinates.schema';
import { Rectangle } from '@/entities/canvas';
import { MODE_DRAW } from '@/utils/constant.utils';
import { twMerge } from 'tailwind-merge';
import { Spinner } from '@/components/Loading/Spinner';
import { ImageDimension } from '@/type/detection.types';
import { Button } from '@/components/Button';
import { IconEraser } from '@tabler/icons-react';
import {
  useBoxCoordinates,
  useDenormalizePolygonPoints,
  usePolygonPoints,
  usePolygonScalePoints,
} from '@/hooks/moderation.hooks';
import { TResultObjectSchema } from '@/schemas/vehicule-check-result/result-object.schema';
import { TFunction } from 'i18next';

type ModerationCanvasProps = {
  t?: TFunction<undefined>;
  elementClass?: string;
  imgSrc: string;
  setRectangle?: Dispatch<TResultImageCoordinatesSchema>;
  setPolygon?: (polygon: TResultImageCoordinatesSchema) => void;
  mode: string;
  initialCoordinates?: TResultImageCoordinatesSchema;
  originalBoundingBox?: Array<number>;
  selectedResultObject?: TResultObjectSchema;
  imageClassName?: string;
  onImageLoading?: (loading: boolean) => void;
};

export const ModerationReportCanvas = ({
  t,
  elementClass,
  imgSrc,
  setRectangle,
  setPolygon,
  mode,
  initialCoordinates,
  originalBoundingBox,
  selectedResultObject,
  imageClassName,
  onImageLoading,
}: ModerationCanvasProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imageRef = useRef<HTMLImageElement | null>(null);

  const [isDrawingRectangle, setIsDrawingRectangle] = useState(false);
  const [isImageLoading, setIsImageLoading] = useState(true);
  const [imageDimension, setImageDimension] = useState<ImageDimension | null>(
    null,
  );
  const [startRectanglePoint, setStartRectanglePoint] = useState<{
    x: number;
    y: number;
  }>({
    x: 0,
    y: 0,
  });
  const [polygonPoints, setPolygonPoints] = useState<
    { x: number; y: number }[]
  >([]);
  const [isPolygonClosed, setIsPolygonClosed] = useState(false);

  const isModerated = initialCoordinates?.moderated;

  const { getCoordinates: getBoxCoordinates } = useBoxCoordinates({
    isModerated,
    coordinates: initialCoordinates,
    imageDimension,
  });

  const { getPoints: initialPolygonPoints } = usePolygonPoints({
    coordinates: initialCoordinates,
  });

  const { getDenormalizedCoordinates } = useDenormalizePolygonPoints({
    originalBoundingBox,
    coordinates: initialCoordinates,
    imageDimension,
  });

  const { getScalePoints: getPolygonScalePoints } = usePolygonScalePoints({
    isModerated,
    polygonPoints: initialPolygonPoints,
    denormalizePolygonPoints: getDenormalizedCoordinates,
  });

  useEffect(() => {
    onImageLoading?.(isImageLoading);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isImageLoading]);

  const drawInitialElement = useCallback(() => {
    const { canvas, ctx } = getCanvasContext(canvasRef) || {};
    if (!canvas || !ctx) return;

    if (initialCoordinates) {
      if (mode === MODE_DRAW.RECTANGLE) {
        const box = getBoxCoordinates();
        const rect = {
          startX: (box?.x / 100) * canvas?.width,
          startY: (box?.y / 100) * canvas?.height,
          width: (box?.width / 100) * canvas?.width,
          height: (box?.height / 100) * canvas?.height,
        };

        drawRectangle(
          ctx,
          rect?.startX,
          rect?.startY,
          rect?.width,
          rect?.height,
          'red',
        );
      }
      if (mode === MODE_DRAW.POLYGON && initialPolygonPoints) {
        const scaledPoints = getPolygonScalePoints(canvas);

        // if (scaledPoints?.length) {
        //   setIsPolygonClosed(true);
        //   drawPolygon({
        //     ctx,
        //     points: scaledPoints,
        //     color: 'red',
        //     closePath: true,
        //   });
        // }

        setIsPolygonClosed(true);
        drawPolygon({
          ctx,
          points:
            scaledPoints?.length > 0 ? scaledPoints : initialPolygonPoints,
          color: 'red',
          closePath: true,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedResultObject,
    initialCoordinates,
    mode,
    getBoxCoordinates,
    initialPolygonPoints,
    getPolygonScalePoints,
    setIsPolygonClosed,
  ]);

  useEffect(() => {
    const img = imageRef.current;
    if (!img) return;

    // Set loading to true when imgSrc changes
    setIsImageLoading(true);

    const handleLoaded = () => {
      drawInitialElement();
      // Set loading to false when image has loaded and canvas is initialized
      setIsImageLoading(false);
      setImageDimension({
        width: img.naturalWidth,
        height: img.naturalHeight,
      });
    };

    img.onload = handleLoaded;

    // Handle case where image is already cached
    if (img.complete) {
      handleLoaded();
    }
  }, [imgSrc]);

  // Separate effect to handle initialElement changes
  useEffect(() => {
    const img = imageRef.current;
    if (img && img.complete) {
      // Image is already loaded, redraw immediately
      initializeCanvas(canvasRef, imageRef);
      drawInitialElement();
    }
  }, [initialCoordinates, mode, drawInitialElement]);

  const clearCanvas = () => {
    const { canvas, ctx } = getCanvasContext(canvasRef) || {};
    if (!canvas || !ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  };

  useEffect(() => {
    if (polygonPoints.length === 0) return;
    const { canvas, ctx } = getCanvasContext(canvasRef) || {};
    if (!canvas || !ctx) return;
    drawPolygon({
      ctx,
      points: polygonPoints,
      color: 'red',
      showDots: !isPolygonClosed,
    });
  }, [polygonPoints, isPolygonClosed]);

  useEffect(() => {
    const handleUndoPolygonPoint = (e: KeyboardEvent) => {
      if (!(e.ctrlKey || e.metaKey) || e.key !== 'z') return;
      if (
        mode !== MODE_DRAW.POLYGON ||
        isPolygonClosed ||
        polygonPoints.length === 0
      )
        return;

      e.preventDefault();
      clearCanvas();
      setPolygonPoints((prev) => prev.slice(0, -1));
    };

    window.addEventListener('keydown', handleUndoPolygonPoint);
    return () => window.removeEventListener('keydown', handleUndoPolygonPoint);
  }, [mode, isPolygonClosed, polygonPoints.length]);

  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const { x, y } = getScaledCoordinates(e.clientX, e.clientY, canvasRef);

    if (mode === MODE_DRAW.POLYGON) {
      if (isPolygonClosed) {
        setPolygonPoints([]);
        setIsPolygonClosed(false);
        clearCanvas();
      }
      setPolygonPoints((prevPoints) => [...prevPoints, { x, y }]);
    } else if (mode === MODE_DRAW.RECTANGLE) {
      setStartRectanglePoint({ x, y });
      setIsDrawingRectangle(true);
    }
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (mode === MODE_DRAW.POLYGON) return;
    if (!isDrawingRectangle) return;

    const { canvas, ctx } = getCanvasContext(canvasRef) || {};
    if (!canvas || !ctx) return;

    initializeCanvas(canvasRef, imageRef);

    const { x: currentX, y: currentY } = getScaledCoordinates(
      e.clientX,
      e.clientY,
      canvasRef,
    );

    const width = currentX - startRectanglePoint?.x;
    const height = currentY - startRectanglePoint?.y;
    drawRectangle(
      ctx,
      startRectanglePoint?.x,
      startRectanglePoint?.y,
      width,
      height,
      'red',
    );
  };

  const handleMouseUp = (e: React.MouseEvent<HTMLCanvasElement>) => {
    setIsDrawingRectangle(false);
    const { x: endXRect, y: endYRect } = getScaledCoordinates(
      e.clientX,
      e.clientY,
      canvasRef,
    );
    const newRectangle: Rectangle = {
      startX: startRectanglePoint?.x,
      startY: startRectanglePoint?.y,
      endX: endXRect,
      endY: endYRect,
    };
    const yoloCoordinates = convertRectangePointToYolo(
      elementClass,
      newRectangle,
      null,
      canvasRef,
    );
    if (
      yoloCoordinates?.width &&
      yoloCoordinates?.height &&
      yoloCoordinates?.xCenter &&
      yoloCoordinates?.yCenter
    ) {
      setRectangle?.(
        initialCoordinates
          ? { ...yoloCoordinates, elNumber: initialCoordinates?.elNumber }
          : yoloCoordinates,
      );
    }
  };

  const handleDoubleClick = () => {
    if (mode === MODE_DRAW.RECTANGLE) return;

    const { canvas, ctx } = getCanvasContext(canvasRef) || {};
    if (!canvas || !ctx) return;

    if (polygonPoints.length > 2) {
      initializeCanvas(canvasRef, imageRef);
      drawPolygon({
        ctx,
        points: polygonPoints,
        color: 'red',
        closePath: true,
      });
      setIsPolygonClosed(true);
      const yoloCoordinates = convertRectangePointToYolo(
        elementClass,
        null,
        polygonPoints,
        canvasRef,
        false,
      );
      if (yoloCoordinates?.point?.length > 0) {
        setPolygon?.({
          ...yoloCoordinates,
          point: [...yoloCoordinates.point],
          isDamaged: initialCoordinates?.isDamaged,
        });
      }
      setPolygonPoints([]);
    }
  };

  const eraseCurrent = () => {
    setPolygonPoints([]);
    clearCanvas();
  };

  return (
    <div className="relative inline-block">
      <img
        ref={imageRef}
        src={imgSrc}
        alt="Overlay"
        loading="lazy"
        decoding="async"
        className={twMerge(
          'w-full h-auto',
          imageClassName,
          isImageLoading && 'min-h-[50vh] opacity-0',
        )}
      />

      <canvas
        className={twMerge(
          'absolute top-0 left-0 cursor-crosshair',
          isImageLoading && 'opacity-0',
        )}
        ref={canvasRef}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onDoubleClick={handleDoubleClick}
      />

      <div
        className={twMerge(
          'hidden justify-end mt-2',
          mode === MODE_DRAW.POLYGON && polygonPoints?.length > 0 && 'flex',
        )}
      >
        <Button
          buttonClassName="bg-red-F8275A/[92%] px-2 cursor-pointer"
          text={t?.('common:erase')}
          onClick={eraseCurrent}
          leftIcon={<IconEraser stroke={2} />}
        />
      </div>

      {isImageLoading && <Spinner />}
    </div>
  );
};
